import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { round } from "utilities/math/math";
import { isNullOrUndefined } from "utilities/object/object";
import { isInPortal } from "utilities/portal/portal";
import { getTaxRateBreakdownForPriceBreakdown } from "utilities/tax/tax.utilities";

import {
    type ILineItemPriceBreakdownInfo,
    LineItemPriceBreakdownInfo,
} from "commonComponents/entity/lineItem/LineItemPriceBreakdown/LineItemPriceBreakdownBase.types";
import { type TaxGroupServiceListItemExtraData } from "commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";
import {
    type ICostLineItem,
    type IMarkupLineItem,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { TaxMethod } from "entity/tax/common/tax.types";
import { type TaxRateBreakdownInfo } from "entity/tax/common/TaxRateBreakdown/TaxRateBreakdown.api.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

const isBuilder = isInPortal({ builder: true });

export const getDefaultLeftAlignment = (
    isBuilder: boolean,
    isFlatFee: boolean,
    canViewLineItems: boolean,
) => {
    return (!isBuilder && !canViewLineItems) || isFlatFee;
};

export const getShowPriceOnly = (
    isFlatFee: boolean,
    flatFeeTaxGroupId: number | undefined,
    taxMethod: TaxMethod | undefined,
) => {
    const taxGroupId = flatFeeTaxGroupId ?? NoTaxId;
    const method = taxMethod ?? TaxMethod.None;

    return isFlatFee && (taxGroupId === NoTaxId || method === TaxMethod.None);
};

export const getDefaultCostPriceShowBreakdown = (
    taxMethod: TaxMethod | undefined,
    isOwnerPriceEntity: boolean | undefined,
    priceBreakdown: ILineItemPriceBreakdownInfo,
) => {
    return (
        (!isOwnerPriceEntity || taxMethod !== TaxMethod.None) &&
        (!isNullOrUndefined(priceBreakdown.builderCost) ||
            (priceBreakdown.taxRateBreakdownInfo?.taxRateBreakdownItems?.length ?? 0) > 0)
    );
};

export const calculateTotalMargin = (
    hasTaxes: boolean,
    builderCost: number | undefined,
    subtotal: number | undefined,
    ownerPrice: number | undefined,
) => {
    if (hasTaxes) {
        return isNullOrUndefined(subtotal) || subtotal === 0
            ? 0
            : round(((subtotal - (builderCost ?? 0)) / subtotal) * 100, 2);
    }
    return isNullOrUndefined(ownerPrice) || ownerPrice === 0
        ? 0
        : round(((ownerPrice - (builderCost ?? 0)) / ownerPrice) * 100, 2);
};

export const calculateTotalPrice = (
    showPriceOnly: boolean,
    taxMethod: TaxMethod,
    subtotal: number,
    totalTax: number,
    taxOverride: number = 0,
) => {
    let totalPrice = subtotal;
    if (!showPriceOnly && taxMethod === TaxMethod.Exclusive) {
        totalPrice += totalTax + taxOverride;
    }
    return totalPrice;
};

export const getFlatFeePriceBreakdownWithNoTaxes = (builderCost: number, ownerPrice: number) => {
    const priceBreakdown = new LineItemPriceBreakdownInfo();
    priceBreakdown.builderCost = builderCost;
    priceBreakdown.ownerPrice = ownerPrice;
    priceBreakdown.totalMarkup = ownerPrice - builderCost;
    priceBreakdown.totalMargin = calculateTotalMargin(
        false,
        priceBreakdown.builderCost,
        priceBreakdown.subtotal,
        priceBreakdown.ownerPrice,
    );
    return priceBreakdown;
};

export const getPriceBreakdownWithNoTaxes = (lineItems: IMarkupLineItem[]) => {
    const priceBreakdown = new LineItemPriceBreakdownInfo();
    priceBreakdown.builderCost =
        lineItems?.reduce((total, lineItem) => total + lineItem.builderCost, 0) ?? 0;
    priceBreakdown.totalMarkup =
        lineItems?.reduce((total, lineItem) => total + lineItem.markupAmount, 0) ?? 0;
    priceBreakdown.totalPrice =
        lineItems?.reduce((total, lineItem) => total + lineItem.ownerPrice, 0) ?? 0;
    priceBreakdown.ownerPrice =
        lineItems?.reduce((total, lineItem) => total + lineItem.ownerPrice, 0) ?? 0;
    priceBreakdown.totalMargin = calculateTotalMargin(
        false,
        priceBreakdown.builderCost,
        priceBreakdown.subtotal,
        priceBreakdown.ownerPrice,
    );
    return priceBreakdown;
};

export const getFlatFeePriceBreakdown = (
    showPriceOnly: boolean,
    flatFeePrice: number | undefined,
    flatFeeBuilderCost: number | undefined,
    flatFeeTaxGroupId: number | undefined,
    taxMethod: TaxMethod,
    taxRateBreakdownInfo: TaxRateBreakdownInfo | undefined,
    taxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[] | undefined,
    isReadOnly: boolean = false,
) => {
    const priceBreakdown = new LineItemPriceBreakdownInfo();
    const subtotal = flatFeePrice ?? 0;
    const builderCost = flatFeeBuilderCost ?? 0;
    priceBreakdown.subtotal = subtotal;
    priceBreakdown.builderCost = builderCost;
    priceBreakdown.totalMarkup = subtotal - builderCost;

    if (!showPriceOnly) {
        priceBreakdown.taxRateBreakdownInfo =
            (!isBuilder || isReadOnly) && !isNullOrUndefined(taxRateBreakdownInfo)
                ? taxRateBreakdownInfo
                : getTaxRateBreakdownForPriceBreakdown<ICostLineItem>(
                      [],
                      true,
                      false,
                      taxGroups,
                      taxMethod,
                      flatFeeTaxGroupId,
                      subtotal,
                  );
    }

    priceBreakdown.totalPrice = calculateTotalPrice(
        showPriceOnly,
        taxMethod,
        subtotal,
        priceBreakdown.taxRateBreakdownInfo?.totalTax ?? 0,
        priceBreakdown.taxRateBreakdownInfo?.taxOverride ?? 0,
    );

    priceBreakdown.totalMargin = calculateTotalMargin(
        true,
        priceBreakdown.builderCost,
        priceBreakdown.subtotal,
        undefined,
    );

    return priceBreakdown;
};

export const getMarkupLineItemPriceBreakdown = (
    showPriceOnly: boolean,
    lineItems: IMarkupLineItem[],
    taxMethod: TaxMethod,
    taxRateBreakdownInfo: TaxRateBreakdownInfo | undefined,
    taxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[] | undefined,
    subtotalOverride: number | undefined,
    isReadOnly: boolean = false,
) => {
    const priceBreakdown = new LineItemPriceBreakdownInfo();
    const builderCost = lineItems.reduce((total, lineItem) => total + lineItem.builderCost, 0);
    const totalMarkup = lineItems.reduce((total, lineItem) => total + lineItem.markupAmount, 0);
    const subtotal =
        subtotalOverride === undefined
            ? lineItems.reduce((total, lineItem) => total + lineItem.ownerPrice, 0)
            : subtotalOverride;

    priceBreakdown.subtotal = subtotal;
    priceBreakdown.builderCost = builderCost;
    priceBreakdown.totalMarkup = totalMarkup;

    if (!showPriceOnly) {
        priceBreakdown.taxRateBreakdownInfo =
            !isNullOrUndefined(taxRateBreakdownInfo) && (!isBuilder || isReadOnly)
                ? taxRateBreakdownInfo
                : getTaxRateBreakdownForPriceBreakdown(
                      lineItems,
                      false,
                      true,
                      taxGroups,
                      taxMethod,
                  );
    }

    priceBreakdown.totalPrice = calculateTotalPrice(
        showPriceOnly,
        taxMethod,
        subtotal,
        priceBreakdown.taxRateBreakdownInfo?.totalTax ?? 0,
        priceBreakdown.taxRateBreakdownInfo?.taxOverride ?? 0,
    );

    priceBreakdown.totalMargin = calculateTotalMargin(
        true,
        priceBreakdown.builderCost,
        priceBreakdown.subtotal,
        undefined,
    );

    return priceBreakdown;
};

export const getCostLineItemPriceBreakdown = (
    showPriceOnly: boolean,
    isOwnerPriceEntity: boolean,
    lineItems: ICostLineItem[],
    taxMethod: TaxMethod,
    taxRateBreakdownInfo: TaxRateBreakdownInfo | undefined,
    taxGroups: BTSelectItem<TaxGroupServiceListItemExtraData>[] | undefined,
    newlyInvoicedAmount: number,
    isReadOnly: boolean = false,
) => {
    const priceBreakdown = new LineItemPriceBreakdownInfo();

    let subtotal = 0;
    let builderCost = undefined;
    if (isOwnerPriceEntity) {
        subtotal = lineItems.reduce((total, lineItem) => total + lineItem.builderCost, 0);
        subtotal = subtotal + newlyInvoicedAmount;
    } else {
        builderCost = lineItems.reduce((total, lineItem) => total + lineItem.builderCost, 0)!;
        subtotal = builderCost;
    }
    priceBreakdown.subtotal = subtotal;
    priceBreakdown.builderCost = builderCost;
    if (!showPriceOnly) {
        priceBreakdown.taxRateBreakdownInfo =
            !isBuilder || isReadOnly
                ? taxRateBreakdownInfo
                : getTaxRateBreakdownForPriceBreakdown(
                      lineItems,
                      false,
                      isOwnerPriceEntity,
                      taxGroups,
                      taxMethod,
                  );
    }

    priceBreakdown.totalPrice = calculateTotalPrice(
        showPriceOnly,
        taxMethod,
        subtotal,
        priceBreakdown.taxRateBreakdownInfo?.totalTax ?? 0,
        priceBreakdown.taxRateBreakdownInfo?.taxOverride ?? 0,
    );

    priceBreakdown.totalMargin = calculateTotalMargin(
        true,
        priceBreakdown.builderCost,
        priceBreakdown.subtotal,
        undefined,
    );

    return priceBreakdown;
};
